import gql from 'graphql-tag';
import { DataProxy } from 'apollo-cache';
import {
  ListTimelineEntriesQuery,
  ListTimelineEntriesQueryVariables,
  PageType,
  MeasurementSystem,
  Page,
} from '../../../types/__generated__';
import useFullPageQuery from '../useFullPageQuery';

export const ListTimelineDocument = gql`
  query ListTimelineEntries(
    $after: String
    $type: PageType!
    $slug: Slug!
    $measurementSystem: MeasurementSystem
  ) {
    page(type: $type, slug: $slug) {
      id
      legacyId
      owner {
        name
        avatar
      }
      timeline(after: $after) {
        nodes {
          id
          legacyId
          media {
            ... on ImageMedia {
              id
              url
              legacyId
            }
          }
          message
          type
          createdAt
          refreshRequestedTime
          fitnessActivity {
            activityId
            title
            activityType
            mapPolyline
            distance(measurementSystem: $measurementSystem) {
              unit
              value
            }
            elevation(measurementSystem: $measurementSystem) {
              unit
              value
            }
            duration {
              unit
              value
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  }
`;

export default function useListTimelineEntries(
  measurementSystem?: MeasurementSystem,
) {
  return useFullPageQuery<ListTimelineEntriesQuery>(ListTimelineDocument, {
    variables: { measurementSystem },
  });
}

const getEmptyPage = (slug: string, type: string) => ({
  id: '',
  legacyId: '',
  type: type.toUpperCase() as PageType,
  slug,
  url: '',
  title: '',
});

export function readTimelineEntries(
  pageType: string,
  pageSlug: string,
  cache: DataProxy,
  measurementSystem?: MeasurementSystem,
) {
  const result = cache.readQuery<
    ListTimelineEntriesQuery,
    ListTimelineEntriesQueryVariables
  >({
    query: ListTimelineDocument,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
      measurementSystem,
    },
  });

  const previousPage = result?.page ?? getEmptyPage(pageSlug, pageType);
  const previousOwner = result?.page?.owner ?? {};
  const previousTimeline = result?.page?.timeline ?? { totalCount: 0 };
  const previousTimelineNodes = result?.page?.timeline?.nodes ?? [];
  const previousPageInfo = result?.page?.timeline?.pageInfo ?? {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  };

  return {
    previousPage,
    previousOwner,
    previousTimeline,
    previousTimelineNodes,
    previousPageInfo,
  };
}

export function writeTimelineEntries(
  pageType: string,
  pageSlug: string,
  data: ListTimelineEntriesQuery,
  cache: DataProxy,
) {
  cache.writeQuery<ListTimelineEntriesQuery, ListTimelineEntriesQueryVariables>(
    {
      query: ListTimelineDocument,
      data,
      variables: {
        type: pageType.toUpperCase() as PageType,
        slug: pageSlug,
      },
    },
  );
}

export const fetchPage = (
  after: string,
  fetchMore: Function,
): (() => Promise<void>) => {
  return () =>
    fetchMore({
      variables: { after },
      updateQuery: (
        previousResult: { page?: Page },
        { fetchMoreResult }: { fetchMoreResult?: { page?: Page } },
      ) => {
        if (
          !previousResult.page?.timeline ||
          !fetchMoreResult?.page?.owner ||
          !fetchMoreResult?.page?.timeline
        ) {
          return previousResult;
        }

        const nextResultNodes = [...previousResult.page.timeline.nodes];

        fetchMoreResult.page.timeline.nodes.forEach(newNode => {
          let existingNodeIndex = nextResultNodes.findIndex(
            x => x.id === newNode.id,
          );
          if (existingNodeIndex !== -1) {
            nextResultNodes[existingNodeIndex] = newNode;
          } else {
            nextResultNodes.push(newNode);
          }
        });

        return {
          page: {
            ...previousResult.page,
            owner: fetchMoreResult.page.owner,
            timeline: {
              ...fetchMoreResult.page.timeline,
              pageInfo: fetchMoreResult.page.timeline.pageInfo,
              nodes: nextResultNodes,
            },
          },
        };
      },
    });
};
